/**
 * Social-login (AB#333) pure helpers, shared by the button (URL building) and
 * the /login callback (fragment parsing). React-free on purpose so the
 * selfcheck can import them under `node --experimental-strip-types`.
 */

/**
 * Deep-link redirect target for the DESKTOP flow: GoTrue redirects here and the
 * OS hands `wordpuppi://auth/social#<fragment>` back to the Tauri shell, which
 * forwards the fragment into the admin iframe (see shell.ts + login.tsx). The
 * web flow keeps redirecting to the SPA /login route instead.
 */
export const DEEP_LINK_REDIRECT = 'wordpuppi://auth/social';

/**
 * postMessage envelope the desktop shell uses to forward the GoTrue fragment
 * to the same-origin admin iframe. Kept as bare literals (not a shared package
 * export) because sender and receiver live in different bundles — the strings
 * are the contract. Any change must be mirrored in shell.ts.
 */
export const DEEPLINK_MSG_SOURCE = 'wpp-desktop-deeplink';
export const DEEPLINK_MSG_KIND = 'social-auth';

/** Build GoTrue's authorize URL. `redirectTo` is URL-encoded here. */
export function buildAuthorizeUrl(baseUrl: string, provider: string, redirectTo: string): string {
  return `${baseUrl}/auth/v1/authorize?provider=${provider}&redirect_to=${encodeURIComponent(redirectTo)}`;
}

/**
 * Parse a GoTrue callback fragment (the part after `#`, no leading `#`). Pulls
 * the access token, or a friendly error message if the provider bounced us.
 */
export function parseAuthFragment(fragment: string): { accessToken?: string; error?: string } {
  const params = new URLSearchParams(fragment);
  const errorDescription = params.get('error_description') ?? params.get('error');
  if (errorDescription) {
    return { error: errorDescription.replace(/\+/g, ' ') };
  }
  return { accessToken: params.get('access_token') ?? undefined };
}

/**
 * DESKTOP BOUNCE (AB#333): the desktop flow sends GoTrue back to an allow-listed
 * https `/login?wpp_desktop=1` URL (the custom `wordpuppi://` scheme is NOT in
 * GoTrue's URI allow-list, so it silently falls back to SITE_URL). The web
 * /login page, when it recognizes that marker + a real session/error fragment,
 * hands the fragment back to the app via the `wordpuppi://auth/social#...` deep
 * link (shell.ts's consumer) instead of running the web session exchange.
 *
 * Pure + React-free so the selfcheck can exercise it. `search` is the raw query
 * string (with or without leading `?`), `hash` the raw fragment (with or without
 * leading `#`). `fragment` is null unless the hash actually carries a session or
 * error to forward.
 */
export function parseDesktopBounce(
  search: string,
  hash: string,
): { isDesktop: boolean; fragment: string | null } {
  const isDesktop =
    new URLSearchParams(search.replace(/^\?/, '')).get('wpp_desktop') === '1';
  const frag = hash.replace(/^#/, '');
  const hasPayload =
    frag.includes('access_token') || frag.includes('error_description') || frag.includes('error');
  return { isDesktop, fragment: hasPayload ? frag : null };
}
